import React from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import * as XLSX from "xlsx";
import { Download, LayoutDashboard, Loader2, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { claimsFilterAtom } from "../../state/filter";
import LiveIndicator from "../LiveIndicator";
import { sharedTableStyles } from "../TableStyles";
import { Claim, ClaimStatus, fmt, formatStatus, getStatusBadgeClasses } from "./types";

export const ImagePreview: React.FC<{
    images: string[];
    index: number;
    onClose: () => void;
    onNext: () => void;
    onPrev: () => void;
}> = ({ images, index, onClose, onNext, onPrev }) => {
    React.useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowRight") onNext();
            if (e.key === "ArrowLeft") onPrev();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose, onNext, onPrev]);

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 p-4" onClick={onClose}>
            <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-2 text-sm text-gray-400">
                    <span>
                        {index + 1} / {images.length}
                    </span>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="bg-[#111111] border border-[#2d2d2d] rounded overflow-hidden">
                    <img src={images[index]} alt="" className="w-full max-h-[80vh] object-contain" />
                </div>
                {images.length > 1 && (
                    <div className="flex justify-center gap-3 mt-3">
                        <button
                            onClick={onPrev}
                            className="px-4 py-1.5 bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300 text-sm rounded hover:bg-[#222222] transition-colors"
                        >
                            ← Prev
                        </button>
                        <button
                            onClick={onNext}
                            className="px-4 py-1.5 bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300 text-sm rounded hover:bg-[#222222] transition-colors"
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

type Props = {
    claims: Claim[];
    loading: boolean;
    isRefreshing: boolean;
    onRefresh: () => void;
    onImageClick: (images: string[], index: number) => void;
};

const matchesClaim = (c: Claim, raw: string): boolean => {
    if (!raw.trim()) return true;
    const q = raw.toLowerCase().trim();
    return (
        String(c.id) === q ||
        c.bookingRef.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.phone.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.category.toLowerCase().includes(q) ||
        c.subService.toLowerCase().includes(q) ||
        (c.comments || "").toLowerCase().includes(q) ||
        c.address1.toLowerCase().includes(q) ||
        (c.address2 || "").toLowerCase().includes(q) ||
        c.city.toLowerCase().includes(q) ||
        c.state.toLowerCase().includes(q) ||
        c.zip.toLowerCase().includes(q) ||
        (c.dropAddress1 || "").toLowerCase().includes(q) ||
        (c.dropAddress2 || "").toLowerCase().includes(q) ||
        (c.dropCity || "").toLowerCase().includes(q) ||
        (c.dropState || "").toLowerCase().includes(q) ||
        (c.dropZip || "").toLowerCase().includes(q) ||
        (c.claimStatus || "").toLowerCase().includes(q) ||
        fmt(c.pickupDate).includes(q) ||
        fmt(c.createdAt).includes(q)
    );
};

const ALL_STATUSES: ClaimStatus[] = [
    "REQUESTED",
    "ACCEPTED_PICKUP",
    "PRE_QC",
    "IN_SERVICE",
    "POST_QC",
    "DISPATCHED",
    "DELIVERED",
    "REJECTED",
    "CANCELLED",
];

const ALL_VENDORS = [
    { value: "PERFECTO_CLEANERS_DELHI", label: "Perfecto Cleaners - Delhi" },
    { value: "CLEANSTEPS_LAUNDRY_MUMBAI", label: "Cleansteps Laundry - Mumbai" },
    { value: "WILD_SNEAKER_LAUNDRY_DELHI", label: "Wild Sneaker Laundry - Delhi" },
];

const INPUT = "w-full px-3 py-2 bg-[#1a1a1a] border border-[#2d2d2d] rounded text-sm text-gray-200 focus:outline-none focus:border-blue-600 transition-colors [color-scheme:dark]";
const LABEL = "block text-xs text-gray-500 font-medium uppercase tracking-wide mb-1";

const ClaimsTable: React.FC<Props> = ({ claims, loading, isRefreshing, onRefresh, onImageClick }) => {
    const navigate = useNavigate();
    const [filters, setFilters] = useRecoilState(claimsFilterAtom);

    const { searchText, statusFilter, vendorFilter, fromDate, toDate } = filters;

    const updateFilter = <K extends keyof typeof filters>(key: K, value: (typeof filters)[K]) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const filtered = claims.filter((c: any) => {
        const matchesSearch = matchesClaim(c, searchText);
        const matchesStatus = statusFilter === "ALL" || c.claimStatus === statusFilter;
        const matchesVendor = vendorFilter === "ALL" || c.vendor === vendorFilter;

        let matchesDate = true;
        if (fromDate || toDate) {
            const claimDate = new Date(c.createdAt);
            claimDate.setHours(0, 0, 0, 0);

            if (fromDate) {
                const fd = new Date(fromDate);
                fd.setHours(0, 0, 0, 0);
                if (claimDate < fd) matchesDate = false;
            }
            if (toDate) {
                const td = new Date(toDate);
                td.setHours(0, 0, 0, 0);
                if (claimDate > td) matchesDate = false;
            }
        }

        return matchesSearch && matchesStatus && matchesVendor && matchesDate;
    });

    const exportToExcel = () => {
        const rows = filtered.map((c: any) => {
            const fullPickup = [c.address1, c.address2, c.city, c.state, c.zip].filter(Boolean).join(", ");
            const fullDrop =
                [c.dropAddress1, c.dropAddress2, c.dropCity, c.dropState, c.dropZip].filter(Boolean).join(", ") ||
                fullPickup;

            const dateToFormat = c.statusUpdatedAt || c.updatedAt;
            const deliveredDate = c.claimStatus === "DELIVERED" && dateToFormat ? fmt(dateToFormat) : " - ";

            return {
                "Claim ID": c.id,
                "Care+ ID": c.bookingRef,
                "Created Date": fmt(c.createdAt),
                Status: formatStatus(c.claimStatus),
                Vendor: c.vendor ? c.vendor.replace(/_/g, " ") : "Unassigned",
                "Pickup Date": fmt(c.pickupDate),
                "Delivered Date": deliveredDate,
                Category: c.category,
                "Sub Service": c.subService,
                Comment: c.comments,
                Customer: c.name,
                Phone: c.phone,
                Email: c.email,
                "Pickup Address 1": c.address1,
                "Pickup Address 2": c.address2 || "",
                "Pickup City": c.city,
                "Pickup State": c.state,
                "Pickup Zip": c.zip,
                "Full Pickup Address": fullPickup,
                "Drop Address 1": c.dropAddress1 || c.address1,
                "Drop Address 2": c.dropAddress2 || c.address2 || "",
                "Drop City": c.dropCity || c.city,
                "Drop State": c.dropState || c.state,
                "Drop Zip": c.dropZip || c.zip,
                "Full Drop Address": fullDrop,
            };
        });
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Claims");
        XLSX.writeFile(wb, `claims_${new Date().toISOString().split("T")[0]}.xlsx`);
    };

    const handleRowClick = (claim: Claim) => {
        navigate(`/service-dashboard/${claim.id}`);
    };

    const columns: TableColumn<Claim>[] = [
        {
            name: "Service Request ID",
            selector: (r) => r.id,
            sortable: true,
            width: "90px",
            cell: (r) => <span className="font-mono text-xs">{r.id}</span>,
        },
        {
            name: "Care+ ID",
            selector: (r) => r.bookingRef,
            sortable: true,
            width: "100px",
            cell: (r) => <span className="font-mono text-sm">{r.bookingRef}</span>,
        },
        {
            name: "Created Date",
            selector: (r) => r.createdAt,
            sortable: true,
            minWidth: "110px",
            grow: 1,
            cell: (r) => <span className="text-sm">{fmt(r.createdAt)}</span>,
        },
        {
            name: "Status",
            selector: (r) => r.claimStatus || "",
            sortable: true,
            width: "140px",
            cell: (r) => <span className={getStatusBadgeClasses(r.claimStatus)}>{formatStatus(r.claimStatus)}</span>,
        },
        {
            name: "Category",
            selector: (r) => r.category,
            sortable: true,
            minWidth: "110px",
            grow: 1,
        },
        {
            name: "Customer",
            selector: (r) => r.name,
            sortable: true,
            minWidth: "120px",
            grow: 1,
        },
        {
            name: "Phone",
            selector: (r) => r.phone,
            minWidth: "130px",
            grow: 1,
            cell: (r) => <span className="font-mono text-sm">{r.phone}</span>,
        },
        {
            name: "Pickup Date",
            selector: (r) => r.pickupDate,
            sortable: true,
            minWidth: "110px",
            grow: 1,
            cell: (r) => <span className="text-sm">{fmt(r.pickupDate)}</span>,
        },
        {
            name: "Delivered Date",
            selector: (r) => r.statusUpdatedAt || r.updatedAt || "",
            sortable: true,
            minWidth: "110px",
            grow: 1,
            cell: (r) => {
                const dateToFormat = r.statusUpdatedAt || r.updatedAt;
                return (
                    <span className="text-sm">
                        {r.claimStatus === "DELIVERED" && dateToFormat ? fmt(dateToFormat) : " - "}
                    </span>
                );
            },
        },
        {
            name: "Files",
            minWidth: "100px",
            grow: 1,
            cell: (r) =>
                r.files?.length > 0 ? (
                    <div className="flex items-center gap-1 py-1">
                        {r.files.slice(0, 2).map((f, i) => (
                            <button
                                key={i}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onImageClick(r.files, i);
                                }}
                            >
                                <img src={f} alt="" className="w-8 h-8 object-cover rounded border border-[#2d2d2d]" />
                            </button>
                        ))}
                        {r.files.length > 2 && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onImageClick(r.files, 2);
                                }}
                                className="w-8 h-8 rounded border border-[#2d2d2d] bg-[#1a1a1a] flex items-center justify-center text-gray-400 text-xs"
                            >
                                +{r.files.length - 2}
                            </button>
                        )}
                    </div>
                ) : (
                    <span className="text-gray-600 text-xs">—</span>
                ),
        },
    ];

    return (
        <div className="space-y-5">
            {/* ── Dashboard Style Filters ────────────────────────────────────── */}
            <div className="bg-[#111111] border border-[#2d2d2d] rounded p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label className={LABEL}>From</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => updateFilter("fromDate", e.target.value)}
                            className={INPUT}
                        />
                    </div>
                    <div>
                        <label className={LABEL}>To</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => updateFilter("toDate", e.target.value)}
                            className={INPUT}
                        />
                    </div>
                    <div>
                        <label className={LABEL}>Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => updateFilter("statusFilter", e.target.value)}
                            className={`${INPUT} cursor-pointer`}
                        >
                            <option value="ALL">All Statuses</option>
                            {ALL_STATUSES.map((s) => (
                                <option key={s} value={s}>
                                    {formatStatus(s)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={LABEL}>Vendor</label>
                        <select
                            value={vendorFilter}
                            onChange={(e) => updateFilter("vendorFilter", e.target.value)}
                            className={`${INPUT} cursor-pointer`}
                        >
                            <option value="ALL">All Vendors</option>
                            {ALL_VENDORS.map((v) => (
                                <option key={v.value} value={v.value}>
                                    {v.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* ── Table Container ────────────────────────────────────────────── */}
            <div className="bg-[#111111] border border-[#2d2d2d] rounded-none overflow-hidden">
                {/* Table Header & Search */}
                <div className="px-5 py-3.5 border-b border-[#2d2d2d] space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                            Service Requests
                            {(searchText || statusFilter !== "ALL" || vendorFilter !== "ALL" || fromDate || toDate) && (
                                <span className="text-xs font-normal text-gray-500 bg-[#1a1a1a] px-2 py-0.5 rounded-full border border-[#2d2d2d]">
                                    Filtered
                                </span>
                            )}
                        </h2>
                        
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                onClick={() => navigate("/")}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300 hover:bg-[#222222] hover:text-white rounded transition-colors"
                            >
                                <LayoutDashboard className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Reporting Dashboard</span>
                                <span className="sm:hidden">Dashboard</span>
                            </button>
                            <button
                                onClick={exportToExcel}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300 hover:bg-[#222222] hover:text-white rounded transition-colors"
                            >
                                <Download className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Export</span>
                            </button>
                            <LiveIndicator isLoading={isRefreshing} onRefresh={onRefresh} />
                        </div>
                    </div>

                    <div className="relative w-full sm:w-96 md:w-[400px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Search by Service Request ID, Care+ ID, Phone..."
                            value={searchText}
                            onChange={(e) => updateFilter("searchText", e.target.value)}
                            className="w-full pl-9 pr-9 py-2 text-sm bg-[#1a1a1a] border border-[#2d2d2d] text-gray-200 placeholder:text-gray-500 rounded focus:outline-none focus:border-[#404040] focus:ring-1 focus:ring-[#404040] transition-colors"
                        />
                        {searchText && (
                            <button
                                onClick={() => updateFilter("searchText", "")}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 p-0.5"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Data Table */}
                <DataTable
                    columns={columns}
                    data={filtered}
                    onRowClicked={handleRowClick}
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    customStyles={sharedTableStyles}
                    progressPending={loading}
                    progressComponent={
                        <div className="flex items-center justify-center gap-2 py-10 text-gray-500 text-sm">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading…
                        </div>
                    }
                    noDataComponent={
                        <div className="py-10 text-center text-gray-600 text-sm">
                            {searchText || fromDate || toDate || statusFilter !== "ALL" || vendorFilter !== "ALL"
                                ? "No claims match your filters."
                                : "No claims found."}
                        </div>
                    }
                    highlightOnHover
                    pointerOnHover
                    responsive
                />
                
                <div className="px-5 py-3 border-t border-[#2d2d2d] flex justify-end">
                    <span className="text-xs text-gray-600">
                        {filtered.length} {filtered.length !== claims.length ? `of ${claims.length}` : ""} records
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ClaimsTable;